// ─── src/components/dashboard/EditEventModal.tsx ─────────────────────────────
//
// Shared "edit a published meetup" modal, used by:
//   - CatalystPortalView (fires PATCH /catalyst-portal/events/:id with JWT)
//   - MeetupAttendeesView, master path (fires PATCH /meetup-events/:id with
//     the admin API key)
//
// The modal is transport-agnostic: it just hands the caller a `patch` object
// containing only the fields the user actually changed, and lets the caller
// decide which endpoint to hit.

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { PublicChip, PublicFieldsLegend } from "./PublicFieldChip";

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * The subset of event fields the modal reads and lets the user change.
 * Both `CatalystEvent` (from catalystPortal) and admin `MeetupEvent` (from
 * types/meetupEvent) already satisfy this shape structurally.
 */
export interface EditableEvent {
  title: string;
  imageUrl: string;
  eventDate: string;
  /** Optional end date for multi-day meetups. Null = single-day. */
  endDate?: string | null;
  startTime: string;
  durationHours: string;
  venue: string;
  mode?: "in_person" | "online" | "hybrid" | null;
  meetingUrl?: string | null;
  governorate: string;
  cityArea: string;
  capacity: number;
  coHostName?: string | null;
  motivation: string;
  agenda?: string;
  speakers?: string | null;
}

/** PATCH payload shape. Matches the backend UpdateMeetupEventDto. */
export interface UpdateMeetupEventPayload {
  title?: string;
  imageUrl?: string;
  eventDate?: string;
  /** Null to clear (revert to single-day); empty string is also treated as clear. */
  endDate?: string | null;
  startTime?: string;
  durationHours?: string;
  venue?: string;
  mode?: "in_person" | "online" | "hybrid";
  /** null / "" clears it. */
  meetingUrl?: string | null;
  governorate?: string;
  cityArea?: string;
  capacity?: number;
  coHostName?: string | null;
  motivation?: string;
  agenda?: string;
  speakers?: string | null;
}

interface EditEventModalProps {
  event: EditableEvent;
  onClose: () => void;
  onSave: (payload: UpdateMeetupEventPayload) => Promise<void>;
  saving: boolean;
  /**
   * Small header sub-line telling the operator where the change will show up
   * (public page for catalysts, admin table for masters). Defaults to the
   * generic "Changes go live immediately on the public page."
   */
  subtitle?: string;
}

// ── Local helper ─────────────────────────────────────────────────────────────

function todayIso() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

// ── Modal ────────────────────────────────────────────────────────────────────

export function EditEventModal({
  event, onClose, onSave, saving, subtitle,
}: EditEventModalProps) {
  // Local draft — only sends changed fields on save.
  const [form, setForm] = useState({
    title: event.title,
    imageUrl: event.imageUrl,
    eventDate: event.eventDate,
    endDate: event.endDate ?? "",
    startTime: event.startTime,
    durationHours: event.durationHours,
    venue: event.venue,
    mode: event.mode ?? "",
    meetingUrl: event.meetingUrl ?? "",
    governorate: event.governorate,
    cityArea: event.cityArea,
    capacity: String(event.capacity),
    coHostName: event.coHostName ?? "",
    motivation: event.motivation,
    agenda: event.agenda ?? "",
    speakers: event.speakers ?? "",
  });
  const [error, setError] = useState<string | null>(null);

  // Keep form in sync when the parent hands us a different event.
  useEffect(() => {
    setForm({
      title: event.title,
      imageUrl: event.imageUrl,
      eventDate: event.eventDate,
      endDate: event.endDate ?? "",
      startTime: event.startTime,
      durationHours: event.durationHours,
      venue: event.venue,
      mode: event.mode ?? "",
      meetingUrl: event.meetingUrl ?? "",
      governorate: event.governorate,
      cityArea: event.cityArea,
      capacity: String(event.capacity),
      coHostName: event.coHostName ?? "",
      motivation: event.motivation,
      agenda: event.agenda ?? "",
      speakers: event.speakers ?? "",
    });
    setError(null);
  }, [event]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const buildPatch = (): UpdateMeetupEventPayload | null => {
    const capacityNum = parseInt(form.capacity, 10);
    if (Number.isNaN(capacityNum) || capacityNum < 0) {
      setError("Capacity must be a whole number ≥ 0.");
      return null;
    }
    if (form.eventDate && form.eventDate < todayIso()) {
      setError("Event date must be today or a future date.");
      return null;
    }
    if (form.endDate && form.endDate < form.eventDate) {
      setError("End date must be on or after the start date.");
      return null;
    }
    // Only include fields the user actually changed. `coHostName` is nullable —
    // an emptied string becomes null so it clears on the server.
    const patch: UpdateMeetupEventPayload = {};
    if (form.title !== event.title) patch.title = form.title.trim();
    if (form.imageUrl !== event.imageUrl) patch.imageUrl = form.imageUrl.trim();
    if (form.eventDate !== event.eventDate) patch.eventDate = form.eventDate;
    // endDate is a nullable field — an emptied string becomes null so it
    // clears on the server (event goes back to single-day).
    const currentEnd = event.endDate ?? "";
    if (form.endDate !== currentEnd) {
      patch.endDate = form.endDate ? form.endDate : null;
    }
    if (form.startTime !== event.startTime) patch.startTime = form.startTime;
    if (form.durationHours !== event.durationHours) patch.durationHours = form.durationHours;
    if (form.venue !== event.venue) patch.venue = form.venue.trim();
    if (form.mode && form.mode !== (event.mode ?? "")) {
      patch.mode = form.mode as "in_person" | "online" | "hybrid";
    }
    const link = form.meetingUrl.trim();
    if (link !== (event.meetingUrl ?? "")) {
      if (link && !/^https?:\/\/\S+\.\S+/i.test(link)) {
        setError("Meeting link must be a full link starting with https://");
        return null;
      }
      patch.meetingUrl = link || null;
    }
    if (form.governorate !== event.governorate) patch.governorate = form.governorate.trim();
    if (form.cityArea !== event.cityArea) patch.cityArea = form.cityArea.trim();
    if (capacityNum !== event.capacity) patch.capacity = capacityNum;
    if ((form.coHostName ?? "") !== (event.coHostName ?? "")) {
      patch.coHostName = form.coHostName.trim() || null;
    }
    if (form.motivation !== event.motivation) patch.motivation = form.motivation;
    if ((form.agenda ?? "") !== (event.agenda ?? "")) patch.agenda = form.agenda;
    if ((form.speakers ?? "") !== (event.speakers ?? "")) {
      patch.speakers = form.speakers.trim() || null;
    }
    return patch;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const patch = buildPatch();
    if (!patch) return;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    try {
      await onSave(patch);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(" · ") : msg ?? "Save failed. Please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="card w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-5 border-b border-brand-border">
          <div>
            <p className="text-white font-bold text-lg">Edit meetup</p>
            <p className="text-brand-muted text-xs">
              {subtitle ?? "Changes go live immediately on the public page."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Every field on this modal is copied onto the public event page,
              so we use the "everything public" variant of the legend
              instead of chipping each label individually. */}
          <PublicFieldsLegend everythingPublic />

          <EditField publicField label="Title" value={form.title} onChange={(v) => set("title", v)} required />
          <EditField publicField label="Cover image URL" value={form.imageUrl} onChange={(v) => set("imageUrl", v)} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <EditField publicField
              label="Start date" type="date" value={form.eventDate}
              onChange={(v) => set("eventDate", v)} min={todayIso()}
            />
            <EditField publicField
              label="End date (multi-day only)" type="date" value={form.endDate}
              onChange={(v) => set("endDate", v)} min={form.eventDate}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <EditField publicField label="Start time" type="time" value={form.startTime} onChange={(v) => set("startTime", v)} />
            <EditField publicField label="Duration (hours)" value={form.durationHours} onChange={(v) => set("durationHours", v)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <EditField publicField label="Governorate" value={form.governorate} onChange={(v) => set("governorate", v)} />
            <EditField publicField label="City / area" value={form.cityArea} onChange={(v) => set("cityArea", v)} />
          </div>

          <EditField publicField label="Venue" value={form.venue} onChange={(v) => set("venue", v)} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-brand-muted text-xs uppercase tracking-wider mb-1.5">
                Mode
                <PublicChip />
              </span>
              <select
                value={form.mode}
                onChange={(e) => set("mode", e.target.value)}
                className="w-full rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-white text-sm focus:border-brand-green focus:outline-none"
              >
                {!form.mode && <option value="">Not set (read from venue)</option>}
                <option value="in_person">In person</option>
                <option value="online">Online</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </label>
            {form.mode !== "in_person" && (
              <label className="block">
                <span className="block text-brand-muted text-xs uppercase tracking-wider mb-1.5">
                  Meeting link (private)
                </span>
                <input
                  type="url"
                  dir="ltr"
                  value={form.meetingUrl}
                  onChange={(e) => set("meetingUrl", e.target.value)}
                  placeholder="https://zoom.us/j/…"
                  className="w-full rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-white text-sm focus:border-brand-green focus:outline-none"
                />
                <span className="mt-1 block text-[11px] text-brand-muted">
                  Not shown on the event page. Sent only to accepted attendees in their acceptance email.
                </span>
              </label>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <EditField publicField
              label="Capacity" type="number" min="0"
              value={form.capacity} onChange={(v) => set("capacity", v)}
            />
            <EditField publicField
              label="Co-host name (optional)"
              value={form.coHostName} onChange={(v) => set("coHostName", v)}
            />
          </div>

          <EditField publicField
            label="Description" value={form.motivation}
            onChange={(v) => set("motivation", v)} multiline rows={4}
          />
          <EditField publicField
            label="Agenda" value={form.agenda}
            onChange={(v) => set("agenda", v)} multiline rows={4}
          />
          <EditField publicField
            label="Speakers (optional)" value={form.speakers}
            onChange={(v) => set("speakers", v)} multiline rows={3}
          />

          {error && (
            <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-brand-border">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary text-sm disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Small text/textarea input used across the modal ──────────────────────────

interface EditFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  min?: string;
  /** Render the shared "عام" chip inline with the label. */
  publicField?: boolean;
}

function EditField({
  label, value, onChange, type = "text", required, multiline, rows, min, publicField,
}: EditFieldProps) {
  const base = "w-full rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-white text-sm focus:border-brand-green focus:outline-none";
  return (
    <label className="block">
      <span className="block text-brand-muted text-xs uppercase tracking-wider mb-1.5">
        {label}
        {publicField && <PublicChip />}
      </span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows ?? 3}
          required={required}
          className={base}
          dir="auto"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          min={min}
          className={base}
          dir="auto"
        />
      )}
    </label>
  );
}
